import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  Cloud,
  CloudOff,
  Image as ImageIcon,
  Layers,
  Palette,
  Redo2,
  RotateCcw,
  Trash2,
  Undo2,
  UserRound,
  X,
} from 'lucide-react';
import { Button, Modal, Spinner } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import { ROUTES } from '@/config/constants';
import { useAuth } from '@/app/providers';
import { useAuthModal } from '@/features/auth/authModalStore';
import { cn } from '@/lib/utils';
import { profileThemeKeys } from '@/features/profile/hooks/useProfileTheme';
import {
  getProfileDraft,
  getPublishedProfileDoc,
  publishProfileDoc,
  resetProfileStudio,
  revertProfileDraft,
  saveProfileDraft,
} from './api/profileStudio';
import { defaultProfileDoc } from './model/profileDoc';
import { newLayer, type BannerEditing } from './model/bannerLayers';
import { useProfileEditor } from './state/profileEditorStore';
import { StudioSectionNav } from './chrome/StudioSectionNav';
import { LayerPanel } from './chrome/LayerPanel';
import { PreviewSwitcher } from './chrome/PreviewSwitcher';
import { AssetsPanel } from './chrome/AssetsPanel';
import { BannerContextStrip, PROFILE_BANNER_CONTEXTS } from './chrome/BannerContextStrip';
import { BannerLayerCanvas } from './canvas/BannerLayerCanvas';
import { LayerInspector } from './inspector/LayerInspector';
import { ProfileInspector } from './inspector/ProfileInspector';
import { ACTIVE_PILL, InspectorSection, Hint } from './inspector/controls';

const AUTOSAVE_IDLE_MS = 2_000;

/**
 * Profile Studio — WINR Studio's second creative surface: your public
 * identity. Left: banner layer stack (Banner mode) or identity map; center:
 * real-component previews or the layered banner canvas; right: the inspector.
 * Drafts autosave; Publish updates every live profile surface immediately.
 */
export function ProfileStudioPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const openAuth = useAuthModal((s) => s.openAuth);

  const doc = useProfileEditor((s) => s.doc);
  const mode = useProfileEditor((s) => s.mode);
  const setMode = useProfileEditor((s) => s.setMode);
  const editSeq = useProfileEditor((s) => s.editSeq);
  const dirty = useProfileEditor((s) => s.dirty);
  const saving = useProfileEditor((s) => s.saving);
  const saveError = useProfileEditor((s) => s.saveError);
  const lastSavedAt = useProfileEditor((s) => s.lastSavedAt);
  const hasPublished = useProfileEditor((s) => s.hasPublished);
  const canUndo = useProfileEditor((s) => s.past.length > 0);
  const canRedo = useProfileEditor((s) => s.future.length > 0);
  const assetsOpen = useProfileEditor((s) => s.assetsOpen);
  const selectedLayerId = useProfileEditor((s) => s.selectedLayerId);
  const store = useProfileEditor;

  const [resetOpen, setResetOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const seeded = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load draft (falling back to published, then default) once per session.
  const { data: loaded, isFetched } = useQuery({
    queryKey: ['profile-studio', 'draft', user?.id ?? ''],
    queryFn: async () => {
      const draft = await getProfileDraft(user!.id);
      if (draft) return { doc: draft, hasPublished: Boolean(await getPublishedProfileDoc(user!.id)) };
      const published = await getPublishedProfileDoc(user!.id);
      return { doc: published ?? defaultProfileDoc(), hasPublished: Boolean(published) };
    },
    enabled: Boolean(user),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (seeded.current) return;
    if (user && !isFetched) return;
    seeded.current = true;
    store.getState().load(loaded?.doc ?? defaultProfileDoc(), loaded?.hasPublished ?? false);
  }, [user, isFetched, loaded, store]);

  // Debounced draft autosave.
  useEffect(() => {
    if (editSeq === 0 || !user) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const s = store.getState();
      if (!s.dirty || s.saving) return;
      s.markSaving();
      saveProfileDraft(user.id, s.doc)
        .then(() => store.getState().markSaved())
        .catch((e: Error) => store.getState().markSaveError(e.message || 'Could not save'));
    }, AUTOSAVE_IDLE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [editSeq, user, store]);

  // Undo/redo keys (the banner canvas adds Del/arrow handling itself).
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const target = e.target as HTMLElement | null;
      const typing =
        target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target?.isContentEditable;
      if (typing || !(e.ctrlKey || e.metaKey)) return;
      if (e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) store.getState().redo();
        else store.getState().undo();
      } else if (e.key.toLowerCase() === 'y') {
        e.preventDefault();
        store.getState().redo();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [store]);

  async function publish(): Promise<void> {
    if (!user) return openAuth();
    setPublishing(true);
    try {
      await publishProfileDoc(user.id, store.getState().doc);
      store.getState().markSaved();
      useProfileEditor.setState({ hasPublished: true });
      void qc.invalidateQueries({ queryKey: profileThemeKeys.published(user.id) });
      toast('Published — your profile is live everywhere', 'ok');
    } catch (e) {
      toast((e as Error).message || 'Could not publish', 'error');
    } finally {
      setPublishing(false);
    }
  }

  async function revert(): Promise<void> {
    if (!user) return;
    try {
      const doc = await revertProfileDraft(user.id);
      store.getState().load(doc ?? defaultProfileDoc(), Boolean(doc));
      toast(doc ? 'Reverted to your published profile' : 'Draft cleared', 'ok');
    } catch (e) {
      toast((e as Error).message || 'Could not revert', 'error');
    }
  }

  async function reset(): Promise<void> {
    if (!user) return;
    setResetOpen(false);
    try {
      await resetProfileStudio(user.id);
      store.getState().load(defaultProfileDoc(), false);
      void qc.invalidateQueries({ queryKey: profileThemeKeys.published(user.id) });
      toast('Profile customization reset to default', 'ok');
    } catch (e) {
      toast((e as Error).message || 'Could not reset', 'error');
    }
  }

  const exit = () => navigate(ROUTES.profileSelf, { replace: true });
  const design = doc.banner.design;

  // Store-agnostic accessors the shared banner components edit through.
  const editing: BannerEditing = {
    layers: design?.layers ?? [],
    selectedId: selectedLayerId,
    onSelect: (id) => store.getState().selectLayer(id),
    onPatchLayer: (id, patch, key) => store.getState().patchLayer(id, patch, key),
    onSetLayers: (layers) => store.getState().setLayers(layers),
  };

  /** Assets panel pick → fill the selected empty image layer, else add a new one. */
  function placeImage(url: string): void {
    const s = store.getState();
    const layers = s.doc.banner.design?.layers ?? [];
    const selected = layers.find((l) => l.id === s.selectedLayerId);
    if (selected?.kind === 'image' && !selected.url) {
      s.patchLayer(selected.id, { url });
    } else {
      const layer = newLayer('image', { url });
      s.setLayers([...layers, layer]);
      s.selectLayer(layer.id);
    }
    if (s.mode !== 'banner') s.setMode('banner');
  }

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-ink-950 text-hi">
      <header className="flex flex-none items-center justify-between gap-3 border-b border-white/[0.08] bg-ink-900 px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <button
            type="button"
            onClick={exit}
            aria-label="Exit studio"
            className="grid h-8 w-8 flex-none place-items-center rounded-lg text-mid transition-colors hover:bg-white/10 hover:text-hi"
          >
            <X size={18} />
          </button>
          <span className="flex flex-none items-center gap-2 text-[13px] font-extrabold text-hi">
            <Palette size={16} className="text-accent" />
            <span className="hidden sm:inline">WINR Studio</span>
          </span>
          <span className="text-white/20">/</span>
          <StudioSectionNav active="profile" />
          <SaveState signedIn={Boolean(user)} saving={saving} dirty={dirty} error={saveError} savedAt={lastSavedAt} />
        </div>

        <div className="flex flex-none items-center gap-1.5">
          <HeadBtn label="Undo (Ctrl+Z)" disabled={!canUndo} onClick={() => store.getState().undo()}>
            <Undo2 size={15} />
          </HeadBtn>
          <HeadBtn label="Redo (Ctrl+Shift+Z)" disabled={!canRedo} onClick={() => store.getState().redo()}>
            <Redo2 size={15} />
          </HeadBtn>
          <HeadBtn label="Assets" onClick={() => store.getState().setAssetsOpen(!assetsOpen)}>
            <ImageIcon size={15} />
          </HeadBtn>
          <span className="mx-1 h-5 w-px bg-white/10" />
          {user && hasPublished && (
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => void revert()}>
              <RotateCcw size={13} /> Revert
            </Button>
          )}
          {user && (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-[#ff8e8e] hover:text-[#ffb3b3]"
              onClick={() => setResetOpen(true)}
            >
              <Trash2 size={13} /> Reset
            </Button>
          )}
          {user ? (
            <Button size="sm" className="gap-1.5" disabled={publishing} onClick={() => void publish()}>
              <Check size={15} strokeWidth={2.6} />
              {publishing ? 'Publishing…' : 'Publish'}
            </Button>
          ) : (
            <Button size="sm" onClick={openAuth}>
              Sign in to save
            </Button>
          )}
        </div>
      </header>

      {user && !isFetched ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          {/* Left rail — mode switch + layers / identity map */}
          <aside className="flex w-60 flex-none flex-col border-r border-white/[0.08] bg-ink-900">
            <div className="flex gap-0.5 border-b border-white/[0.06] p-2">
              <RailMode active={mode === 'profile'} icon={<UserRound size={12} />} onClick={() => setMode('profile')}>
                Identity
              </RailMode>
              <RailMode active={mode === 'banner'} icon={<Layers size={12} />} onClick={() => setMode('banner')}>
                Banner
              </RailMode>
            </div>
            {mode === 'banner' ? (
              <LayerPanel editing={editing} onAddImage={() => store.getState().setAssetsOpen(true)} />
            ) : (
              <div className="flex-1 overflow-y-auto p-3 text-[11.5px] leading-relaxed text-white/40">
                <p className="mb-3">
                  Style your identity on the right — accent, avatar frame, name treatment, emblem —
                  and watch every real surface update live.
                </p>
                <p>
                  Switch to <b className="text-white/70">Banner</b> to design a layered banner that
                  replaces your title-card art everywhere.
                </p>
              </div>
            )}
          </aside>

          {/* Center — previews or the banner workbench (live frames + canvas) */}
          <div className="relative flex min-w-0 flex-1 flex-col">
            {mode === 'banner' ? (
              <>
                <BannerContextStrip
                  design={design}
                  focal={doc.banner.focal}
                  onFocal={(focal) =>
                    store.getState().patch((d) => ({ ...d, banner: { ...d.banner, focal } }), 'focal')
                  }
                  contexts={PROFILE_BANNER_CONTEXTS}
                />
                <BannerLayerCanvas editing={editing} />
              </>
            ) : (
              <PreviewSwitcher />
            )}
            {assetsOpen && (
              <AssetsPanel onClose={() => store.getState().setAssetsOpen(false)} onPlace={placeImage} />
            )}
          </div>

          {/* Right — inspector */}
          <aside className="flex w-72 flex-none flex-col overflow-y-auto border-l border-white/[0.08] bg-ink-900">
            <div className="border-b border-white/[0.05] px-4 py-3 text-[10px] font-extrabold uppercase tracking-[0.8px] text-white/40">
              {mode === 'banner' ? (selectedLayerId ? 'Layer' : 'Banner') : 'Identity'}
            </div>
            {mode === 'banner' ? <LayerInspector editing={editing} /> : <ProfileInspector />}
            {mode === 'banner' && !selectedLayerId && (
              <InspectorSection title="Contexts">
                <Hint>
                  Safe-zone outlines on the canvas show exactly how the chat popup and DM rail crop
                  your banner. Check every context from the Identity tab's preview switcher.
                </Hint>
              </InspectorSection>
            )}
          </aside>
        </div>
      )}

      <Modal open={resetOpen} onClose={() => setResetOpen(false)}>
        <div className="mb-1 text-lg font-extrabold text-hi">Reset profile customization?</div>
        <p className="mb-5 text-[13px] leading-relaxed text-mid">
          This removes your published customization and your draft — every profile surface returns
          to the default title-card look. Your uploaded assets stay in your library.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setResetOpen(false)}>
            Cancel
          </Button>
          <Button className="bg-[#ef4444] text-white hover:bg-[#dc2626]" onClick={() => void reset()}>
            Reset everything
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function SaveState({
  signedIn,
  saving,
  dirty,
  error,
  savedAt,
}: {
  signedIn: boolean;
  saving: boolean;
  dirty: boolean;
  error: string | null;
  savedAt: number | null;
}) {
  if (!signedIn) {
    return (
      <span className="hidden items-center gap-1.5 text-[11px] font-semibold text-lo lg:flex">
        <CloudOff size={13} /> Local only
      </span>
    );
  }
  if (error) {
    return (
      <span className="hidden items-center gap-1.5 text-[11px] font-semibold text-[#ff8e8e] lg:flex" title={error}>
        <AlertTriangle size={13} /> {error}
      </span>
    );
  }
  return (
    <span className={cn('hidden items-center gap-1.5 text-[11px] font-semibold lg:flex', saving || dirty ? 'text-mid' : 'text-lo')}>
      <Cloud size={13} className={saving ? 'animate-pulse' : undefined} />
      {saving ? 'Saving…' : dirty ? 'Unsaved changes' : savedAt ? 'Draft saved' : 'Autosave on'}
    </span>
  );
}

function HeadBtn({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid h-8 w-8 place-items-center rounded-lg text-mid transition-colors hover:bg-white/10 hover:text-hi disabled:opacity-30 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}

function RailMode({
  active,
  icon,
  onClick,
  children,
}: {
  active: boolean;
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-1 items-center justify-center gap-1.5 rounded-md py-1.5 text-[12px] font-bold transition-all',
        active ? ACTIVE_PILL : 'text-white/45 hover:text-white/80',
      )}
    >
      {icon}
      {children}
    </button>
  );
}
